"use client"

import { cn } from "@/lib/utils"
import type { Axis, Bar } from "@/lib/career-track"

/**
 * A career drawn on the page's shared time axis — canvas 27a.
 *
 * "Bar width is tenure, position is recency, the current role carries the teal
 * edge and older roles recede." The receding is what makes a row readable at a
 * glance: the eye lands on the lit bar first and reads leftwards into history.
 *
 * The gridlines belong to the track rather than to the header, because 27a runs
 * them "down through every row" — a scale you can only read at the top of the
 * table is not a scale you can compare two rows against.
 */

/** Three steps of recession; anything older than the third stays at the third. */
const DEPTH = [
  "bg-foreground/[0.04] ring-border",
  "bg-foreground/[0.07] ring-muted-foreground/40",
  "bg-foreground/[0.10] ring-muted-foreground/55",
]

function depthClass(depth: number, total: number) {
  /*
    Counted from the most recent, so the newest of the muted roles is always the
    brightest however many came before it. This was `DEPTH.length - fromEnd`,
    which for the newest role indexed one past the end and fell through to the
    faintest — the recession ran backwards on any track whose roles had all
    ended.
  */
  const fromEnd = total - 1 - depth
  return DEPTH[Math.max(0, DEPTH.length - 1 - fromEnd)]
}

export function CareerAxis({ axis, className }: { axis: Axis; className?: string }) {
  return (
    <div className={cn("relative h-[10px]", className)}>
      {axis.ticks.map((tick) => (
        <span
          key={tick.year}
          className="absolute top-0 translate-x-px font-mono text-[9.5px] leading-none text-muted-foreground"
          style={{ left: `${tick.left}%` }}
        >
          {tick.year}
        </span>
      ))}
      {/* "now" only when the career actually reaches it — a track whose last
          role ended in 2021 ends in 2021. */}
      <span className="absolute right-0 top-0 font-mono text-[9.5px] leading-none text-muted-foreground">
        {axis.isOpen ? "now" : axis.to}
      </span>
    </div>
  )
}

export function CareerTrack({
  bars,
  axis,
  height = 22,
  /** The drill-down draws taller bars and puts the tenure inside them. */
  showTenure,
  /** The role currently being pointed at, from either end of the pairing. */
  activeId,
  /**
   * Supplied by a screen that pairs the track with a list of the same roles.
   * It also changes what the track *is*: with it the bars are controls a reader
   * can tab through, without it the whole track is one image, because eight
   * unlabelled boxes is not something to make a screen reader walk.
   */
  onHoverRole,
  className,
}: {
  bars: Bar[]
  axis: Axis
  height?: number
  showTenure?: boolean
  activeId?: string
  onHoverRole?: (id?: string) => void
  className?: string
}) {
  const interactive = Boolean(onHoverRole)

  return (
    <div
      className={cn("relative", className)}
      style={{ height }}
      role={interactive ? undefined : "img"}
      aria-label={
        interactive
          ? undefined
          : bars.length === 0
            ? "No dated roles on record"
            : `Career: ${bars
                .slice()
                .reverse()
                .map((bar) => `${bar.company ?? "unknown"} ${bar.range}`)
                .join(", ")}`
      }
    >
      {axis.ticks.map((tick) => (
        <span
          key={tick.year}
          aria-hidden
          className="absolute bottom-0 top-0 w-px bg-muted-foreground/35"
          style={{ left: `${tick.left}%` }}
        />
      ))}

      {bars.map((bar) => {
        const isActive = activeId === bar.id
        const content = (
          <>
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-[10px] leading-none",
                bar.isCurrent || isActive ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {bar.company}
            </span>
            {showTenure && (
              <span className="ml-1.5 flex-none font-mono text-[9.5px] leading-none text-muted-foreground">
                {bar.tenure}
              </span>
            )}
          </>
        )

        const shape = cn(
          "absolute top-0 flex items-center overflow-hidden rounded-[3px] px-1.5 ring-1 ring-inset transition-[box-shadow,background-color]",
          bar.isCurrent
            ? "bg-primary/[0.12] shadow-[inset_2px_0_0_var(--primary)] ring-transparent"
            : depthClass(bar.depth, bars.length),
          // Lit rather than recoloured: the ramp still says how old the role is,
          // and the ring says which one you are pointing at.
          isActive && "ring-primary ring-2",
        )
        const style = {
          left: `${bar.left}%`,
          width: `${bar.width}%`,
          height,
        }

        if (!interactive) {
          return <span key={bar.id} aria-hidden className={shape} style={style}>{content}</span>
        }

        return (
          <button
            key={bar.id}
            type="button"
            className={cn(shape, "text-left")}
            style={style}
            aria-label={`${bar.company ?? "Role"}, ${bar.range}`}
            onMouseEnter={() => onHoverRole?.(bar.id)}
            onMouseLeave={() => onHoverRole?.(undefined)}
            onFocus={() => onHoverRole?.(bar.id)}
            onBlur={() => onHoverRole?.(undefined)}
          >
            {content}
          </button>
        )
      })}
    </div>
  )
}


/**
 * The same career as one unbroken track, for the phone — canvas 30d.
 *
 * "Twelve years as one unbroken track cut into four segments by tenure, tapping
 * a segment opens that role below it." There is no shared axis here and no
 * gridlines: on a screen showing one person there is nothing to compare
 * against, so the span is their own and the segments abut. One role is
 * described at a time, which is what removes the duplication of a track and a
 * full ledger saying the same thing twice.
 */
export function ContinuousTrack({
  segments,
  activeId,
  onSelect,
  className,
}: {
  segments: Bar[]
  activeId?: string
  onSelect: (id: string) => void
  className?: string
}) {
  return (
    <div className={cn("relative h-11", className)}>
      {segments.map((segment) => {
        const isActive = activeId === segment.id
        return (
          <button
            key={segment.id}
            type="button"
            onClick={() => onSelect(segment.id)}
            aria-pressed={isActive}
            aria-label={`${segment.company ?? "Role"}, ${segment.range}`}
            className="absolute top-0 h-11 px-[2px] py-[5px]"
            style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
          >
            <span
              className={cn(
                "flex h-[34px] items-center justify-center overflow-hidden rounded-[3px] ring-1 ring-inset transition-colors",
                segment.isCurrent
                  ? "bg-primary/[0.12] ring-primary/40"
                  : "bg-foreground/[0.06] ring-border",
                isActive && "ring-2 ring-primary",
              )}
            >
              <span
                className={cn(
                  "truncate px-1 text-[10px] leading-none",
                  segment.isCurrent || isActive
                    ? "font-medium text-foreground"
                    : "text-muted-foreground",
                )}
              >
                {segment.company}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}
